import type { ScoreWorkoutInput, WorkoutScoreResult } from './types';
import { SCORING_VERSION } from './scoringConfig';
import { estimateOneRepMax } from './estimateOneRepMax';
import { applyInputMultiplier, normalizeToCompetitionLift } from './normalizeExercise';
import { calculateDotsCoefficient } from './dots';
import {
  calculateCategoryPoints,
  calculateReferenceDots,
  normalizedLiftForPoints,
} from './categoryPoints';
import { calculatePullupPoints, pullupBenchmarkE1RmLb, pullupE1RmLb } from './pullupBenchmarks';

/**
 * Calculates the complete score for a workout input.
 *
 * @param input The score workout input parameters.
 * @returns An immutable snapshot of the workout score result.
 */
export function scoreWorkout(input: ScoreWorkoutInput): WorkoutScoreResult {
  // Bodyweight exercises (pull-up, chin-up) use their own benchmark model.
  // Body weight is already part of the resistance, so the DOTS body-weight
  // correction is deliberately not applied - it would double-normalize.
  if ((input.bodyWeightFactor ?? 0) > 0) {
    const estimated1RmLb = pullupE1RmLb(input.bodyWeightLb, input.enteredWeightLb, input.reps);
    const points = calculatePullupPoints({
      bodyWeightLb: input.bodyWeightLb,
      addedWeightLb: input.enteredWeightLb,
      reps: input.reps,
      sex: input.scoringSex,
      difficultyMultiplier: input.normalizationMultiplier,
    });
    // Expressed as the competition-lift (barbell row) 1RM worth the same points,
    // so a pull-up can be compared directly against a row in this category.
    const normalized1RmLb = normalizedLiftForPoints(
      points,
      input.bodyWeightLb,
      input.scoringSex,
      input.category,
    );
    return {
      estimated1RmLb,
      normalized1RmLb,
      // DOTS is not part of bodyweight scoring; recorded as 0 to make that explicit.
      dotsCoefficient: 0,
      categoryReferenceDots: pullupBenchmarkE1RmLb(input.bodyWeightLb, input.scoringSex),
      points,
      scoringVersion: SCORING_VERSION,
      exerciseConfigVersion: 'v1',
    };
  }

  const baseLiftWeight = applyInputMultiplier(input.enteredWeightLb, input.inputWeightMultiplier);
  const estimated1RmLb = estimateOneRepMax(baseLiftWeight, input.reps);
  const normalized1RmLb = normalizeToCompetitionLift(estimated1RmLb, input.normalizationMultiplier);

  const dotsCoefficient = calculateDotsCoefficient(input.bodyWeightLb, input.scoringSex);
  const categoryReferenceDots = calculateReferenceDots(input.category);
  const points = calculateCategoryPoints(
    normalized1RmLb,
    input.bodyWeightLb,
    input.scoringSex,
    input.category,
  );

  return {
    estimated1RmLb,
    normalized1RmLb,
    dotsCoefficient,
    categoryReferenceDots,
    points,
    scoringVersion: SCORING_VERSION,
    exerciseConfigVersion: 'v1',
  };
}
